import * as React from 'react';
import { connect } from 'react-redux';
import cookie from 'react-cookies';
import API from '../../services/API';
import {
  CART_COOKIE_ID,
  CART_SYNC_CHANNEL,
  CART_SYNC_MIN_POLL_GAP_MS,
  CART_SYNC_POLL_INTERVAL_MS,
} from '../../containers/Cart/constants';
import {
  loadCart,
  setCart,
  setCartChangedElsewhere,
} from '../../containers/Cart/actions';
import { cookiesCartExpiresDate, inCart, isSsr, prop } from '../../utilities';

interface CartSyncProps {
  cart: any;
  user: any;
  currentPath: string;
  cartChangedElsewhere: boolean;
  dispatch: any;
}

const CartSync = (props: CartSyncProps) => {
  const { cart, user } = props;

  const revisionRef = React.useRef<string | null>(null);
  const lastPollAtRef = React.useRef(0);
  const inFlightRef = React.useRef(false);
  const channelRef = React.useRef<any>(null);

  const propsRef = React.useRef(props);
  propsRef.current = props;

  const broadcastRevision = React.useCallback((revision) => {
    try {
      if (channelRef.current) {
        channelRef.current.postMessage({ revision });
      }
    } catch (e) {}
  }, []);

  const applyRemoteChange = React.useCallback(async (cartId) => {
    const { currentPath, cartChangedElsewhere, dispatch } = propsRef.current;

    if (inCart(currentPath)) {
      if (!cartChangedElsewhere) {
        dispatch(setCartChangedElsewhere(true));
      }
      return;
    }

    revisionRef.current = null;
    await dispatch(loadCart(cartId));
  }, []);

  const poll = React.useCallback(
    async (force = false) => {
      if (isSsr() || inFlightRef.current) {
        return;
      }

      if (
        !force &&
        Date.now() - lastPollAtRef.current < CART_SYNC_MIN_POLL_GAP_MS
      ) {
        return;
      }

      const cartId = cookie.load(CART_COOKIE_ID);
      if (!cartId) {
        return;
      }

      let syncState;
      inFlightRef.current = true;
      try {
        syncState = await API.getCartSync(cartId);
      } catch (e) {
        return;
      } finally {
        inFlightRef.current = false;
        lastPollAtRef.current = Date.now();
      }

      const { dispatch } = propsRef.current;

      if (!syncState || typeof syncState.exists !== 'boolean') {
        return;
      }

      if (!syncState.exists) {
        cookie.remove(CART_COOKIE_ID, { path: '/' });
        revisionRef.current = null;
        dispatch(setCart(null));
        return;
      }

      if (syncState.cart_id && syncState.cart_id !== cartId) {
        cookie.save(CART_COOKIE_ID, syncState.cart_id, {
          expires: cookiesCartExpiresDate(),
          path: '/',
        });
        revisionRef.current = null;
        await dispatch(loadCart(syncState.cart_id));
        return;
      }

      if (revisionRef.current === null) {
        revisionRef.current = syncState.revision;
        broadcastRevision(syncState.revision);
        return;
      }

      if (revisionRef.current !== syncState.revision) {
        await applyRemoteChange(syncState.cart_id ? syncState.cart_id : cartId);
      }
    },
    [applyRemoteChange, broadcastRevision],
  );

  React.useEffect(() => {
    if (isSsr() || typeof BroadcastChannel === 'undefined') {
      return;
    }

    const channel = new BroadcastChannel(CART_SYNC_CHANNEL);
    channelRef.current = channel;

    channel.onmessage = (event) => {
      const revision = prop(event, 'data.revision', null);

      if (
        !revision ||
        revisionRef.current === null ||
        revision === revisionRef.current
      ) {
        return;
      }

      const cartId = cookie.load(CART_COOKIE_ID);
      if (cartId) {
        applyRemoteChange(cartId);
      }
    };

    return () => {
      channelRef.current = null;
      channel.close();
    };
  }, [applyRemoteChange]);

  React.useEffect(() => {
    if (isSsr() || !user) {
      return;
    }

    const onWake = () => {
      if (document.visibilityState === 'visible') {
        poll();
      }
    };

    const interval = setInterval(onWake, CART_SYNC_POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [user, poll]);

  React.useEffect(() => {
    if (isSsr() || !cart) {
      return;
    }

    revisionRef.current = null;
    poll(true);
  }, [cart, poll]);

  return null;
};

const mapStateToProps = (state) => {
  return {
    cart: state.cart.cart,
    cartChangedElsewhere: !!state.cart.cartChangedElsewhere,
    user: state.auth.user,
    currentPath: prop(state.routing, 'locationBeforeTransitions.pathname', ''),
  };
};

export default connect(mapStateToProps)(CartSync);
